// One app-wide policy for what may be typed into a number field.
//
// `type="number"` is not a numeric filter. Every browser still accepts "e" and "E"
// (scientific notation), "+", and a "-" anywhere in the string; it steps the value on
// a mouse wheel; and it takes any paste at all, quietly landing on an EMPTY value when
// the text does not parse. In an ERP those are not cosmetic: a Qty box that swallows
// "1e5" or loses a pasted "1,234.50" writes the wrong stock or the wrong money.
//
// The guard is installed once, on the document, in the capture phase. Doing it there
// rather than per-field is deliberate — there are ~150 number inputs across 70-odd
// screens, some rendered by the shared `controls/Input`, some by a raw MUI TextField,
// and a rule that has to be remembered at each call site is a rule that will be missed
// on the next screen someone adds. Marking a field `type="number"` is the whole
// contract; the policy below comes with it.
//
// It only ever calls preventDefault, never stopPropagation, so a component's own
// onKeyDown (POS's Enter-to-next-line, the stock request's search loop) still runs.

const SEPARATOR = /[.,]/;

const isNumberInput = (el) =>
  el instanceof HTMLInputElement &&
  el.type === "number" &&
  !el.readOnly &&
  !el.disabled;

// Negatives are default-DENY, the same way authorization is.
//
// Nothing in this product takes a negative through a form: an inventory adjustment
// carries its direction in an Increase/Decrease dropdown, a journal line in the choice
// of debit or credit column, and a quantity or a payment is a magnitude. A "-" in any
// of them is a typo that reaches the server as a real negative. A field that genuinely
// needs one says so with `min` below zero.
const allowsNegative = (el) => {
  const min = el.getAttribute("min");
  return min !== null && min !== "" && parseFloat(min) < 0;
};

// A number input reports value === "" the whole time its raw text is unparseable —
// while "1." is on its way to "1.5", and equally when it holds "1.2.3". The raw text
// can never be read back, so `badInput` is the only evidence that something half-typed
// is sitting in there. That matters for exactly one key: a second separator turns a
// recoverable "1." into a value the app reads as blank, with the box still showing
// digits. Nothing on screen says the number was lost.
const hasSeparator = (el) => el.validity.badInput || SEPARATOR.test(el.value);

const onKeyDown = (e) => {
  const el = e.target;
  if (!isNumberInput(el)) return;

  // Shortcuts (copy, paste, select-all) and every non-printing key — Tab, Enter,
  // Backspace, Delete, the arrows, F-keys — carry a name longer than one character
  // and must pass through untouched.
  if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;

  const key = e.key;
  if (/[0-9]/.test(key)) return;
  if (SEPARATOR.test(key) && !hasSeparator(el)) return;
  if (key === "-" && allowsNegative(el)) return;

  // Everything else: letters (the "fas" that started this), "e"/"E", "+", spaces,
  // currency symbols. None of it belongs in a quantity or an amount.
  e.preventDefault();
};

// A focused number input steps its own value on scroll. Scrolling down a forty-line
// purchase order with the pointer resting over a Qty box silently rewrites that line,
// and nothing on screen marks it — the most dangerous default this element has.
//
// The wheel is cancelled rather than the field blurred: blurring would fire onBlur,
// and on several screens (product variants, order lines) onBlur is what SAVES the row.
// Losing a scroll gesture over one small focused box beats an unintended write.
const onWheel = (e) => {
  const el = e.target;
  if (!isNumberInput(el) || el !== document.activeElement) return;
  e.preventDefault();
};

// React installs its own value setter on the input element, so assigning `el.value`
// updates the DOM without the component ever hearing about it — the box would show the
// cleaned number while state kept the old one. Going through the prototype setter and
// re-dispatching "input" is how the change reaches onChange.
const setNativeValue = (el, value) => {
  const setter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value",
  )?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
};

// "KD 1,234.500" copied off a statement, "1 234,50" out of a spreadsheet: today both
// paste into a number input and leave it blank. Commas and spaces are read as thousands
// grouping and dropped, everything non-numeric goes, and a stray second dot is folded
// into the decimals rather than being allowed to void the whole value.
export const sanitizeNumericText = (text, negativeAllowed = false) => {
  const negative = negativeAllowed && /^\s*-/.test(text);
  const [whole, ...rest] = text.replace(/[^\d.]/g, "").split(".");
  const decimals = rest.join("");
  return `${negative ? "-" : ""}${whole}${rest.length ? `.${decimals}` : ""}`;
};

const handleIncomingText = (e, text) => {
  const el = e.target;
  if (!isNumberInput(el)) return;

  const clean = sanitizeNumericText(text, allowsNegative(el));
  // Already a plain number — let the browser handle it so the caret behaves normally.
  if (clean === text.trim()) return;

  e.preventDefault();
  // The value is replaced whole rather than spliced at the caret: `selectionStart`
  // throws on a number input, so there is no caret position to splice at.
  setNativeValue(el, clean);
};

const onPaste = (e) => handleIncomingText(e, e.clipboardData?.getData("text") ?? "");
const onDrop = (e) => handleIncomingText(e, e.dataTransfer?.getData("text") ?? "");

let installed = false;

// Called once from App. Returns its own uninstaller so React 18's StrictMode
// double-mount in development tears the listeners down instead of stacking them.
export const installNumericInputGuard = () => {
  if (installed || typeof document === "undefined") return () => {};
  installed = true;

  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("paste", onPaste, true);
  document.addEventListener("drop", onDrop, true);
  // passive: false, or the browser ignores preventDefault on a wheel event.
  document.addEventListener("wheel", onWheel, { capture: true, passive: false });

  return () => {
    document.removeEventListener("keydown", onKeyDown, true);
    document.removeEventListener("paste", onPaste, true);
    document.removeEventListener("drop", onDrop, true);
    document.removeEventListener("wheel", onWheel, { capture: true });
    installed = false;
  };
};

// Length cap for a number field, where `maxLength` does not apply.
//
// Only ever truncates text it can actually read: mid-decimal the value reads as "",
// and an unconditional write would wipe the "1." the user is still typing.
export const capNumericLength = (max) => (e) => {
  const v = e.target.value;
  if (v.length > max) e.target.value = v.slice(0, max);
};
